/**
 * @file The states in which an automatic version reload must wait. This module reads
 * the first three itself; the components that own the rest register a guard.
 */
import type { AxiosAdapter, AxiosInstance } from 'axios'
import type Vue from 'vue'

export const UNSAFE_STATES = [
  'api-write-in-flight',
  'unsaved-input',
  'modal-open',
  'drawer-open',
  'payment-submitting',
  'apple-pay-sheet',
  'pin-entry'
] as const

export type UnsafeState = (typeof UNSAFE_STATES)[number]
export type ReloadGuard = () => boolean

const EDITED_ATTRIBUTE = 'data-blytz-edited'
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete'])
const guards = new Set<{ name: UnsafeState; guard: ReloadGuard }>()
const tracked = new WeakSet<AxiosInstance>()
let writesInFlight = 0

/** @returns a function that removes the guard. */
export function registerReloadGuard(name: UnsafeState, guard: ReloadGuard) {
  const entry = { name, guard }
  guards.add(entry)

  return () => {
    guards.delete(entry)
  }
}

/** Registers a guard for as long as the component lives. */
export function guardWhileMounted(
  vm: Vue,
  name: UnsafeState,
  guard: ReloadGuard
) {
  vm.$once('hook:beforeDestroy', registerReloadGuard(name, guard))
}

/**
 * Counts the writes the instance sends until each settles. Reads are left out: a poll
 * that hangs on a lost connection must not block every reload. Idempotent, and an
 * `axios.create()` made afterwards inherits the counting adapter.
 */
export function trackWritesInFlight(instance: AxiosInstance) {
  if (tracked.has(instance)) return
  tracked.add(instance)
  const send = instance.defaults.adapter as AxiosAdapter
  instance.defaults.adapter = async config => {
    if (!WRITE_METHODS.has((config.method ?? 'get').toLowerCase())) {
      return send(config)
    }
    writesInFlight += 1
    try {
      return await send(config)
    } finally {
      writesInFlight -= 1
    }
  }
}

/** The element holds input the user changed: unsafe until it leaves the DOM or the route changes. */
export function markEdited(element: Element) {
  element.setAttribute(EDITED_ATTRIBUTE, '')
}

/** A route change replaces the view, so the edits it held are no longer on screen. */
export function clearEdits(doc: Document = document) {
  doc
    .querySelectorAll(`[${EDITED_ATTRIBUTE}]`)
    .forEach(element => element.removeAttribute(EDITED_ATTRIBUTE))
}

/**
 * Marks the element of every user edit.
 * @param doc the document to listen on.
 * @param isUserEvent skips script-dispatched events, such as v-mask's synthetic `input`.
 */
export function trackEdits(
  doc: Document = document,
  isUserEvent = (event: Event) => event.isTrusted
) {
  const onEdit = (event: Event) => {
    if (isUserEvent(event) && event.target instanceof Element) {
      markEdited(event.target)
    }
  }
  doc.addEventListener('input', onEdit, true)
  doc.addEventListener('change', onEdit, true)

  return () => {
    doc.removeEventListener('input', onEdit, true)
    doc.removeEventListener('change', onEdit, true)
  }
}

/** @returns the names of the states that block a reload right now. */
export function unsafeReloadStates(doc: Document = document): UnsafeState[] {
  const active: UnsafeState[] = []
  if (writesInFlight > 0) active.push('api-write-in-flight')
  if (doc.querySelector(`[${EDITED_ATTRIBUTE}]`)) active.push('unsaved-input')
  if (doc.body?.classList.contains('modal-open')) active.push('modal-open')
  guards.forEach(({ name, guard }) => {
    if (guard()) active.push(name)
  })

  return active
}

export function isUnsafeToReload(doc: Document = document): boolean {
  return unsafeReloadStates(doc).length > 0
}

/** Test seam: the counter and the guard registry are module state. */
export function __resetReloadSafety() {
  guards.clear()
  writesInFlight = 0
}
